import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { RootState } from '../redux/store'
import { resetState, updateTokens } from '../redux/slices/auth.slice'
import { Mutex } from 'async-mutex'

// Create a mutex to prevent multiple refresh attempts
const mutex = new Mutex()

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const createBaseQuery = (baseUrl?: string): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> => {
  const baseQuery = fetchBaseQuery({
    baseUrl: baseUrl || apiUrl,
    prepareHeaders: (headers, { getState }) => {
      const { access_token } = (getState() as RootState).authReducer
      if (access_token) {
        headers.set('authorization', `Bearer ${access_token}`)
      }
      return headers
    },
  })

  return async (args, api, extraOptions) => {
    // Wait for any ongoing refresh to complete
    await mutex.waitForUnlock()
    
    let result = await baseQuery(args, api, extraOptions)

    if (result.error && result.error.status === 401) {
      // Check if we're already refreshing
      if (!mutex.isLocked()) {
        const release = await mutex.acquire()
        
        try {
          console.log('Access token expired, attempting refresh...')
          
          const { refresh_token } = (api.getState() as RootState).authReducer
          
          if (refresh_token) {
            // Try to refresh the token
            const refreshResult = await baseQuery(
              {
                url: '/auth/refresh',
                method: 'POST',
                body: { refresh_token },
              },
              api,
              extraOptions
            )

            if (refreshResult.data) {
              const refreshData = refreshResult.data as any
              
              // Update tokens in Redux
              api.dispatch(updateTokens({
                access_token: refreshData.access_token,
                refresh_token: refreshData.refresh_token || refresh_token,
                tokenExpiry: refreshData.tokenExpiry
              }))

              // Update localStorage
              const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
              const updatedUser = {
                ...currentUser,
                access_token: refreshData.access_token,
                refresh_token: refreshData.refresh_token || refresh_token,
                tokenExpiry: refreshData.tokenExpiry
              }
              localStorage.setItem('user', JSON.stringify(updatedUser))

              console.log('Token refreshed successfully')
              
              // Retry the original request with new token
              result = await baseQuery(args, api, extraOptions)
            } else {
              console.log('Token refresh failed, logging out user')
              api.dispatch(resetState())
            }
          } else {
            console.log('No refresh token available, logging out user')
            api.dispatch(resetState())
          }
        } finally {
          release()
        }
      } else {
        // Wait for the ongoing refresh and retry
        await mutex.waitForUnlock()
        result = await baseQuery(args, api, extraOptions)
      }
    }

    return result
  }
}

interface CreateApiServiceOptions {
  reducerPath: string
  tagTypes?: string[]
  baseUrl?: string
  endpoints: (build: any) => any
}

/**
 * Factory function to create API services with automatic authentication handling
 * 
 * @param options Configuration for the API service
 * @returns RTK Query API service with automatic auth, token refresh, and 401 handling
 * 
 * @example
 * export const articlesAPI = createApiService({
 *   reducerPath: 'articlesAPI',
 *   tagTypes: ['Article'],
 *   endpoints: (builder) => ({
 *     getAllArticles: builder.query({
 *       query: () => '/article/all'
 *     })
 *   })
 * })
 */
export const createApiService = ({ 
  reducerPath, 
  tagTypes = [], 
  baseUrl, 
  endpoints 
}: CreateApiServiceOptions) => {
  return createApi({
    reducerPath,
    tagTypes,
    baseQuery: createBaseQuery(baseUrl),
    endpoints
  })
}

// Export the base query for services that need custom configuration
export const createAuthenticatedBaseQuery = createBaseQuery